import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Disc3, Music } from 'lucide-react';
import { AudioFeatures } from '@meradio/shared-types';
import { useAppDispatch } from '@/store/store';
import { setCurrentVideo, addToQueue } from '@/features/player/playerSlice';
import { analyzeVideo } from '@/services/audio.api';
import { getVideosByArtist, ArtistVideo } from '@/services/artists.api';
import VideoTable from '@/components/table/VideoTable';
import AddToCratePopover from '@/features/crates/AddToCratePopover';

interface DiscographyVideo {
  id: string;
  videoId: string;
  channelId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  publishedAt: Date;
  dateAdded: Date;
  createdAt: Date;
  updatedAt: Date;
  isLiked?: boolean;
  parsedAlbum?: string | null;
  channel?: {
    id?: string;
    channelId?: string;
    name?: string;
    thumbnailUrl?: string;
  };
}

interface AlbumSection {
  album: string | null;
  videos: DiscographyVideo[];
  newest: number;
}

const toDiscographyVideo = (item: ArtistVideo): DiscographyVideo => ({
  id: item.id,
  videoId: item.videoId,
  channelId: item.channel?.channelId || item.channel?.id || item.channelId || '',
  title: item.title,
  description: item.description,
  thumbnailUrl: item.thumbnailUrl,
  publishedAt: new Date(item.publishedAt),
  dateAdded: new Date(item.publishedAt),
  createdAt: new Date(item.publishedAt),
  updatedAt: new Date(item.publishedAt),
  isLiked: item.isLiked,
  parsedAlbum: item.parsedAlbum ?? null,
  channel: item.channel,
});

const ArtistPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const dispatch = useAppDispatch();

  const [items, setItems] = useState<ArtistVideo[]>([]);
  const [audioFeatures, setAudioFeatures] = useState<Record<string, AudioFeatures>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crateTarget, setCrateTarget] = useState<{ videoId: string; title: string } | null>(null);

  const artistName = name ? decodeURIComponent(name) : '';

  const videos = useMemo(() => items.map(toDiscographyVideo), [items]);

  const channelNames = useMemo(() => {
    const names = new Set<string>();
    videos.forEach(video => {
      if (video.channel?.name) names.add(video.channel.name);
    });
    return Array.from(names);
  }, [videos]);

  const sections = useMemo<AlbumSection[]>(() => {
    const groups = new Map<string, AlbumSection>();
    videos.forEach(video => {
      const key = video.parsedAlbum ?? ' singles';
      const existing = groups.get(key);
      const published = video.publishedAt.getTime();
      if (existing) {
        existing.videos.push(video);
        existing.newest = Math.max(existing.newest, published);
      } else {
        groups.set(key, { album: video.parsedAlbum ?? null, videos: [video], newest: published });
      }
    });
    return Array.from(groups.values()).sort((a, b) => {
      if (a.album === null) return 1;
      if (b.album === null) return -1;
      return b.newest - a.newest;
    });
  }, [videos]);

  useEffect(() => {
    if (!artistName) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getVideosByArtist(artistName)
      .then(result => {
        if (cancelled) return;
        setItems(result);
        const features: Record<string, AudioFeatures> = {};
        result.forEach(item => {
          if (item.audioFeatures) features[item.videoId] = item.audioFeatures;
        });
        setAudioFeatures(features);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load artist');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [artistName]);

  const handlePlayVideo = (video: DiscographyVideo) => {
    dispatch(setCurrentVideo(video as any));
  };

  const handleAddToQueue = (video: DiscographyVideo) => {
    dispatch(addToQueue(video as any));
  };

  const handleAnalyze = async (video: DiscographyVideo) => {
    try {
      await analyzeVideo(video.videoId);
    } catch {
      console.error('Failed to queue analysis');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-neutral-300 dark:bg-bg-dark-tertiary" />
          <div className="h-64 rounded-xl bg-neutral-200 dark:bg-bg-dark-secondary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-6">
          <p className="text-danger-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 pt-4 md:p-6 max-w-7xl mx-auto">
      <div className="px-4 md:px-0 mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Disc3 className="w-8 h-8 text-meradio-primary flex-shrink-0" />
          <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary truncate">
            {artistName}
          </h1>
        </div>
        <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary">
          {videos.length} {videos.length === 1 ? 'track' : 'tracks'}
          {channelNames.length > 0 && <span> · {channelNames.join(', ')}</span>}
        </p>
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-12">
          <Music className="w-12 h-12 text-neutral-400 dark:text-text-dark-tertiary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-text-dark-primary mb-2">
            No tracks found
          </h3>
        </div>
      ) : (
        <div className="space-y-10">
          {sections.map(section => (
            <section key={section.album ?? ' singles'}>
              <h2 className="px-4 md:px-0 text-xl font-semibold text-meradio-dark dark:text-text-dark-primary mb-3">
                {section.album ? (
                  <Link
                    to={`/albums/${encodeURIComponent(section.album)}`}
                    className="hover:text-meradio-primary hover:underline"
                  >
                    {section.album}
                  </Link>
                ) : (
                  'Singles'
                )}
              </h2>
              <VideoTable
                videos={section.videos}
                audioFeatures={audioFeatures}
                onPlayVideo={handlePlayVideo}
                onAddToQueue={handleAddToQueue}
                onAnalyze={handleAnalyze}
                onAddToCrate={video => setCrateTarget({ videoId: video.videoId, title: video.title })}
              />
            </section>
          ))}
        </div>
      )}

      <AddToCratePopover
        videoId={crateTarget?.videoId ?? null}
        videoTitle={crateTarget?.title}
        open={crateTarget !== null}
        onClose={() => setCrateTarget(null)}
      />
    </div>
  );
};

export default ArtistPage;
